import { useEffect, useRef, useState } from "react";
import type { SelectOption } from "@/components/SelectField";

// A searchable single-select combobox: type to filter the options or open it to
// browse the full list (search + dropdown), the typeahead sibling of SelectField.
// Controlled via `value`/`onChange` (empty string = nothing selected). Closes on
// Escape and on an outside click. Presentational building block — the caller
// decides what a selection does.
interface SearchSelectProps {
  label: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** Shown when nothing is selected. */
  placeholder?: string;
}

export function SearchSelect({
  label,
  options,
  value,
  onChange,
  placeholder,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  // Close on Escape or a click outside, but only while open.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q))
    : options;

  // While open the input reflects the search query; when closed it shows the
  // selected option's label (or the placeholder via the empty string).
  const display = open ? query : (selected?.label ?? "");

  return (
    <div ref={containerRef} className="flex w-full flex-col gap-1.5 text-left">
      <label className="text-sm font-medium text-brk-text">{label}</label>
      <div className="relative">
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          autoComplete="off"
          value={display}
          placeholder={placeholder}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (value) onChange("");
          }}
          className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 pr-9 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
        />
        {selected ? (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={() => {
              onChange("");
              setQuery("");
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-brk-dim transition-colors hover:text-brk-text"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-brk-dim"
          >
            ▾
          </span>
        )}

        {open && (
          <ul
            role="listbox"
            className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-brk-border bg-brk-panel py-1 shadow-lg"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-brk-dim">No matches</li>
            ) : (
              filtered.map((o) => (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(o.value);
                      setQuery("");
                      setOpen(false);
                    }}
                    className={`block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-brk-bg ${
                      o.value === value
                        ? "font-medium text-brk-accent"
                        : "text-brk-text"
                    }`}
                  >
                    {o.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
